"use client";

import { MessageBubble } from "./components/MessageBubble/MessageBubble";
import { MessageInput } from "./components/MessageInput";
import { UiBackToBottom } from "./components/ui/UiBackToBottom";
import {
  type ScrollToEndOptions,
  type ScrollToRefreshHandler,
  UiScrollToRefresh,
} from "./components/ui/UiScrollToRefresh";
import { ChatboxProvider, type ChatboxStore, useChatboxStore } from "./store";
import { isNearBottom } from "./utils/isNearBottom";
import type { FormMessageData } from "@/model/form";
import { MessagingMessageType } from "@/model/messaging";
import { cn } from "@deputy-ai/common";
import { useSubscribe } from "@deputy-ai/common/observable";
import { useMemoizedFn, useThrottleFn } from "ahooks";
import {
  type PropsWithChildren,
  type UIEvent,
  useEffect,
  useMemo,
  useRef,
} from "react";

export type ChatboxComponentProps = PropsWithChildren<{
  className?: string;
  store: ChatboxStore;
}>;

export const Chatbox = (props: ChatboxComponentProps) => {
  const { children, className, store } = props;

  return (
    <ChatboxProvider store={store}>
      <div
        className={cn("relative flex justify-center overflow-auto", className)}
      >
        <ChatboxContent>{children}</ChatboxContent>
      </div>
    </ChatboxProvider>
  );
};

const ChatboxContent = ({ children }: PropsWithChildren) => {
  const {
    use: {
      newCount,
      visibleMessages,
      lastMessage,
      showBackBottom,
      options,
      noMoreMessages,
    },
    bind: { init, setShowBackBottom, setNewCount, loadMore },
  } = useChatboxStore();

  useSubscribe(init, [init]);

  const showInputBox = useMemo(() => {
    if (options?.input?.hidden) {
      return false;
    }
    if (lastMessage?.payload.messageType === MessagingMessageType.Form) {
      const formData = lastMessage?.payload.data as unknown as FormMessageData;
      return !formData?.submitted;
    }
    return true;
  }, [lastMessage, options?.input?.hidden]);

  function handleRefresh() {
    return loadMore();
  }

  const onScroll = useThrottleFn(
    (el: UIEvent) => {
      if (isNearBottom(el.target as HTMLDivElement, 3)) {
        if (newCount > 0 && isNearBottom(el.target as HTMLDivElement, 0.5)) {
          setNewCount(0);
        }
        setShowBackBottom(false);
        return;
      }
      setShowBackBottom(true);
    },
    { wait: 100 }
  );

  const scrollToEnd = useMemoizedFn((opts?: ScrollToEndOptions) => {
    if (!scrollerRef.current) {
      return;
    }
    if (!showBackBottom || opts?.force) {
      scrollerRef.current.scrollToEnd(opts);
      if (showBackBottom) {
        setShowBackBottom(false);
        setNewCount(0);
      }
    }
  });

  const scrollerRef = useRef<ScrollToRefreshHandler>(null);

  useEffect(() => {
    const wrapper = scrollerRef.current?.wrapperRef.current;

    if (!wrapper) {
      return;
    }

    if (
      lastMessage?.participant?.externalId ===
      options?.participants?.myself?.externalId
    ) {
      setTimeout(() => scrollToEnd({ force: true }), 50);
      return;
    }

    if (isNearBottom(wrapper, 2)) {
      scrollToEnd({ force: true });
      return;
    }

    setNewCount(newCount + 1);
    setShowBackBottom(true);
  }, [lastMessage]);

  const maxWidth = options?.maxWidth ?? 800;

  return (
    <div className={cn("relative flex h-full grow flex-col items-center")}>
      <UiScrollToRefresh
        ref={scrollerRef}
        className="chatbox-messages w-full grow gap-y-2"
        list={visibleMessages}
        disabledRefresh={noMoreMessages}
        renderItem={(msg) => (
          <div
            key={msg.uid}
            className="w-full"
            style={{
              maxWidth: `${maxWidth}px`,
            }}
          >
            <MessageBubble
              containerRef={scrollerRef.current?.wrapperRef}
              message={msg}
            />
          </div>
        )}
        onRefresh={handleRefresh}
        onScroll={onScroll.run}
      />
      {showBackBottom && (
        <UiBackToBottom
          count={newCount}
          onClick={() => scrollToEnd({ animated: false, force: true })}
          className={options?.input?.compact ? "bottom-16" : "bottom-[8rem]"}
        />
      )}
      <div
        style={{
          maxWidth: `${maxWidth}px`,
        }}
      >
        {children}
      </div>
      {showInputBox && <MessageInput />}
    </div>
  );
};
